import React, { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Box, Typography, Button, Alert, MenuItem, Select, FormControl, InputLabel } from '@mui/material';

const CitizenUploadDocuments = () => {
  const { citizenId } = useParams();
  const [selectedFile, setSelectedFile] = useState(null);
  const [documentType, setDocumentType] = useState('');
  const [preview, setPreview] = useState(null);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [uploads, setUploads] = useState([]);

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      setSelectedFile(file);
      setPreview(URL.createObjectURL(file));
      setMessage(null);
      setError(null);
    }
  };

  const handleUpload = async () => {
    if (!selectedFile || !documentType) {
      setError('Please select both a file and document type.');
      return;
    }

    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('documentType', documentType);

    try {
      const response = await fetch(`/api/citizens/${citizenId}/documents/upload`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (response.ok) {
        setMessage('Document uploaded successfully.');
        setError(null);
        setUploads(prev => [...prev, data]);
      } else {
        setError(data.message || 'Upload failed.');
        setMessage(null);
      }
    } catch (err) {
      console.error(err);
      setError('Server error while uploading.');
      setMessage(null);
    }
  };

  useEffect(() => {
    const fetchUploads = async () => {
      try {
        const res = await fetch(`/api/citizens/${citizenId}/documents`);
        const data = await res.json();
        if (res.ok) setUploads(data.documents || []);
      } catch (err) {
        console.error('Failed to fetch documents:', err);
      }
    };
    fetchUploads();
  }, [citizenId]);

  return (
    <Box className="p-6 bg-white dark:bg-[#232326] text-black dark:text-white min-h-screen">
      <Typography variant="h5" className="mb-4 text-[#22733c] dark:text-[#8cffbc]">
        Upload Document for Citizen ID: {citizenId}
      </Typography>

      <FormControl fullWidth className="mb-4">
        <InputLabel id="doc-type-label">Document Type</InputLabel>
        <Select
          labelId="doc-type-label"
          value={documentType}
          onChange={(e) => setDocumentType(e.target.value)}
          fullWidth
        >
          <MenuItem value="">Select Document Type</MenuItem>
          <MenuItem value="opd_slip">OPD Slip</MenuItem>
          <MenuItem value="investigation_report">Investigation Report</MenuItem>
          <MenuItem value="prescription">Prescription</MenuItem>
          <MenuItem value="other">Other</MenuItem>
        </Select>
      </FormControl>

      <input type="file" accept="image/*" onChange={handleFileChange} />

      {preview && (
        <div className="my-4">
          <img src={preview} alt="Preview" className="rounded shadow max-w-xs" />
        </div>
      )}

      <Button
        variant="contained"
        color="success"
        onClick={handleUpload}
        className="mt-2"
      >
        Upload Document
      </Button>

      {message && <Alert severity="success" className="mt-4">{message}</Alert>}
      {error && <Alert severity="error" className="mt-4">{error}</Alert>}

      <Box className="mt-8">
        <Typography variant="h6" className="mb-2 text-[#22733c] dark:text-[#8cffbc]">
          Uploaded Documents
        </Typography>
        {uploads.length === 0 ? (
          <Typography>No documents uploaded yet.</Typography>
        ) : (
          uploads.map((doc, index) => (
            <Box key={index} className="mb-3">
              <Typography variant="subtitle2">{doc.documentType} - {new Date(doc.uploadedAt).toLocaleString()}</Typography>
              <img src={doc.url} alt={doc.documentType} className="w-40 border rounded" />
            </Box>
          ))
        )}
      </Box>
    </Box>
  );
};

export default CitizenUploadDocuments;
